import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  Divider,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  PlayArrow as FunnelIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  CheckCircle as CheckedIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { FilterCriteria, Campaign, LeadFinderStats, LeadFinderPreviewRow } from '../types/outreach';

const OutreachLeadFinder: React.FC = () => {
  const navigate = useNavigate();

  // Filter States
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [websiteQuery, setWebsiteQuery] = useState('');
  const [gmoStatus, setGmoStatus] = useState<'any' | 'ran' | 'never'>('any');
  const [snovStatus, setSnovStatus] = useState<'any' | 'ran' | 'never'>('any');
  const [reoonStatus, setReoonStatus] = useState<'any' | 'ran' | 'never'>('any');
  const [hasInmailers, setHasInmailers] = useState<'any' | 'yes' | 'no'>('any');
  const [stateName, setStateName] = useState('');

  // Group Label and Schedule
  const [groupLabel, setGroupLabel] = useState('');
  const [scheduleType, setScheduleType] = useState<'manual' | 'weekly_mon' | 'monthly_1st' | 'quarterly'>('manual');

  // Master Data
  const [allTags, setAllIndustryTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Stats & Preview States
  const [stats, setStats] = useState<LeadFinderStats | null>(null);
  const [previewRows, setPreviewRows] = useState<LeadFinderPreviewRow[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Fetch Tags
  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);
      try {
        const response = await ApiService.getIndustryTags(null, 2000);
        if (response && response.data) {
          const names = response.data.map((t: any) => t.name).filter(Boolean);
          setAllIndustryTags(Array.from(new Set(names)));
        }
      } catch (err) {
        console.error('Error loading industry tags:', err);
      } finally {
        setIsLoadingTags(false);
      }
    };
    fetchTags();
  }, []);

  // Assemble Filter Criteria for API
  const buildFiltersArray = useCallback((): FilterCriteria[] => {
    const filters: FilterCriteria[] = [];

    // Industry Tags
    if (selectedTags.length > 0) {
      filters.push({
        field: 'industry_tag',
        operator: 'in',
        value: selectedTags,
        logic: 'AND',
      });
    }

    // Website Contains
    if (websiteQuery.trim()) {
      filters.push({
        field: 'website',
        operator: 'like',
        value: websiteQuery.trim(),
        logic: 'AND',
      });
    }

    // State USA (stored as state_usa ID but matching by name from LEFT JOIN state table)
    if (stateName.trim()) {
      filters.push({
        field: 'state_usa',
        operator: 'equals',
        value: stateName.trim(), // Server's buildFilterQuery will resolve
        logic: 'AND',
      });
    }

    // GMO Last Ran Status
    if (gmoStatus === 'ran') {
      filters.push({
        field: 'last_ran_gmo',
        operator: 'is_not_null',
        logic: 'AND',
      });
    } else if (gmoStatus === 'never') {
      filters.push({
        field: 'last_ran_gmo',
        operator: 'is_null',
        logic: 'AND',
      });
    }

    // Snov Last Ran Status
    if (snovStatus === 'ran') {
      filters.push({
        field: 'last_ran_snov',
        operator: 'is_not_null',
        logic: 'AND',
      });
    } else if (snovStatus === 'never') {
      filters.push({
        field: 'last_ran_snov',
        operator: 'is_null',
        logic: 'AND',
      });
    }

    // Reoon Last Ran Status
    if (reoonStatus === 'ran') {
      filters.push({
        field: 'last_ran_reoon',
        operator: 'is_not_null',
        logic: 'AND',
      });
    } else if (reoonStatus === 'never') {
      filters.push({
        field: 'last_ran_reoon',
        operator: 'is_null',
        logic: 'AND',
      });
    }

    // Has Inmailers
    if (hasInmailers === 'yes') {
      filters.push({
        field: 'in_mailer',
        operator: 'is_not_null',
        logic: 'AND',
      });
      filters.push({
        field: 'in_mailer',
        operator: 'not_equals',
        value: '0',
        logic: 'AND',
      });
    } else if (hasInmailers === 'no') {
      filters.push({
        field: 'in_mailer',
        operator: 'is_null',
        logic: 'AND',
      });
    }

    return filters;
  }, [selectedTags, websiteQuery, gmoStatus, snovStatus, reoonStatus, hasInmailers, stateName]);

  // Fetch Stats & Preview
  const handleFetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    setError(null);
    try {
      const filters = buildFiltersArray();
      const response = await ApiService.getLeadFinderStats(filters);
      if (response && response.success) {
        setStats(response.stats);
        setPreviewRows(response.preview || []);
      } else {
        setError('Failed to fetch statistics for selected filters.');
      }
    } catch (err: any) {
      console.error('Error fetching lead finder stats:', err);
      setError(err.message || 'Error occurred while loading matching leads.');
    } finally {
      setIsLoadingStats(false);
    }
  }, [buildFiltersArray]);

  // Initial Fetch & update when filters change
  useEffect(() => {
    handleFetchStats();
  }, [handleFetchStats]);

  // Submit / Funnel & Create Group
  const handleFunnelGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupLabel.trim()) {
      setError('Please provide a Group Label before funneling.');
      return;
    }

    if (!stats || stats.total_domains === 0) {
      setError('No domains match your current filter selection. Refine filters first.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const filters = buildFiltersArray();

      // 1. Create Saved Campaign
      const createResponse = await ApiService.createCampaign({
        name: groupLabel.trim(),
        tag_filters: filters,
        schedule_type: scheduleType,
        status: 'active',
      });

      if (!createResponse.success) {
        throw new Error(createResponse.message || 'Failed to create campaign group.');
      }

      setSuccessMsg(`Group "${groupLabel}" created with ${createResponse.domains_count} domains.`);

      // 2. If 'manual' (Run Immediately), run campaign and navigate to process page
      if (scheduleType === 'manual') {
        const runResponse = await ApiService.runCampaign(createResponse.campaignId);
        if (runResponse.success && runResponse.sessionId) {
          // Navigate to the process page with the new sessionId
          navigate(`/outreach/process?session=${runResponse.sessionId}`);
        } else {
          setError('Campaign created, but failed to launch immediate run. You can run it manually from the campaigns tab.');
        }
      } else {
        // Scheduled Campaign, stay and notify
        setGroupLabel('');
        setScheduleType('manual');
        alert(`Successfully created and scheduled campaign "${groupLabel}"! You can manage it under the Campaigns tab.`);
        handleFetchStats();
      }
    } catch (err: any) {
      console.error('Error funneling group:', err);
      setError(err.message || 'An error occurred while creating or running the campaign.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setWebsiteQuery('');
    setGmoStatus('any');
    setSnovStatus('any');
    setReoonStatus('any');
    setHasInmailers('any');
    setStateName('');
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* LEFT: Multi-Criteria Filter Query Panel */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: '12px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon color="primary" /> Lead Query Builder
              </Typography>
              <Button size="small" variant="text" onClick={clearFilters} color="inherit">
                Reset
              </Button>
            </Box>

            <Grid container spacing={2}>
              {/* Industry Tags Multi-select */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="tags-autocomplete"
                  options={allTags}
                  loading={isLoadingTags}
                  value={selectedTags}
                  onChange={(event, newValue) => setSelectedTags(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Industry Tags"
                      placeholder="Select Tags"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingTags ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 1 }}
                />
              </Grid>

              {/* Website Contains */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website domain"
                  placeholder="e.g. google.com"
                  size="small"
                  value={websiteQuery}
                  onChange={(e) => setWebsiteQuery(e.target.value)}
                />
              </Grid>

              {/* USA State Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State Name"
                  placeholder="e.g. California"
                  size="small"
                  value={stateName}
                  onChange={(e) => setStateName(e.target.value)}
                />
              </Grid>

              {/* Has Inmailers */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="inmailers-label">Inmailers Count</InputLabel>
                  <Select
                    labelId="inmailers-label"
                    value={hasInmailers}
                    label="Inmailers Count"
                    onChange={(e) => setHasInmailers(e.target.value as any)}
                  >
                    <MenuItem value="any">Any (Include 0)</MenuItem>
                    <MenuItem value="yes">Has Inmailers (&gt; 0)</MenuItem>
                    <MenuItem value="no">No Inmailers (0 / Null)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* GMO API Verification Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="gmo-label">GMO Verified</InputLabel>
                  <Select
                    labelId="gmo-label"
                    value={gmoStatus}
                    label="GMO Verified"
                    onChange={(e) => setGmoStatus(e.target.value as any)}
                  >
                    <MenuItem value="any">Any Run Status</MenuItem>
                    <MenuItem value="ran">Has Been Ran</MenuItem>
                    <MenuItem value="never">Never Ran</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Snov API Verification Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="snov-label">Snov Verified</InputLabel>
                  <Select
                    labelId="snov-label"
                    value={snovStatus}
                    label="Snov Verified"
                    onChange={(e) => setSnovStatus(e.target.value as any)}
                  >
                    <MenuItem value="any">Any Run Status</MenuItem>
                    <MenuItem value="ran">Has Been Ran</MenuItem>
                    <MenuItem value="never">Never Ran</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Reoon API Verification Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="reoon-label">Reoon Verified</InputLabel>
                  <Select
                    labelId="reoon-label"
                    value={reoonStatus}
                    label="Reoon Verified"
                    onChange={(e) => setReoonStatus(e.target.value as any)}
                  >
                    <MenuItem value="any">Any Run Status</MenuItem>
                    <MenuItem value="ran">Has Been Ran</MenuItem>
                    <MenuItem value="never">Never Ran</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isLoadingStats ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={handleFetchStats}
                  disabled={isLoadingStats}
                >
                  Apply Filters & Search
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Group Labeling and Funneling Actions */}
          <Paper sx={{ 
            p: 3, 
            mt: 3,
            borderRadius: '12px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0',
            background: 'linear-gradient(135deg, #ffffff 0%, #fcfdfe 100%)'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FunnelIcon color="primary" /> Funnel Leads to Campaign
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {successMsg && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
                {successMsg}
              </Alert>
            )}

            <form onSubmit={handleFunnelGroup}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Campaign Group Label (Name)"
                  placeholder="e.g. Q3-Fintech-Inmailers"
                  size="small"
                  required
                  value={groupLabel}
                  onChange={(e) => setGroupLabel(e.target.value)}
                />

                <FormControl fullWidth size="small">
                  <InputLabel id="schedule-label">Campaign Schedule</InputLabel>
                  <Select
                    labelId="schedule-label"
                    value={scheduleType}
                    label="Campaign Schedule"
                    onChange={(e) => setScheduleType(e.target.value as any)}
                  >
                    <MenuItem value="manual">Run Immediately (Manual)</MenuItem>
                    <MenuItem value="weekly_mon">Weekly (Mondays at 8 AM)</MenuItem>
                    <MenuItem value="monthly_1st">Monthly (1st of Month)</MenuItem>
                    <MenuItem value="quarterly">Quarterly (Every 3 Months)</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={isSubmitting || isLoadingStats || !stats || stats.total_domains === 0}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <FunnelIcon />}
                  sx={{ 
                    mt: 1,
                    py: 1.2,
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #11b756 0%, #0c8a3c 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0f9f4a 0%, #0a7332 100%)',
                    }
                  }}
                >
                  {scheduleType === 'manual' ? 'Funnel & Launch Run' : 'Schedule Group Campaign'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>

        {/* RIGHT: Live KPI Yield Preview & Matching Preview Grid */}
        <Grid item xs={12} md={7}>
          {/* Real-time KPI Dashboards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Target Domains Count */}
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 4px 15px rgba(37, 99, 235, 0.12)', 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                color: '#ffffff'
              }}>
                <CardContent sx={{ p: '16px !important' }}>
                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 'medium' }} gutterBottom>
                    Total Domains
                  </Typography>
                  {isLoadingStats ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats?.total_domains ?? 0}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Inmailers Count */}
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 4px 15px rgba(124, 58, 237, 0.12)', 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: '#ffffff'
              }}>
                <CardContent sx={{ p: '16px !important' }}>
                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 'medium' }} gutterBottom>
                    Inmailers Count
                  </Typography>
                  {isLoadingStats ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats?.total_inmailers ?? 0}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Email Validation Yields */}
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.12)', 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff'
              }}>
                <CardContent sx={{ p: '16px !important' }}>
                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 'medium' }} gutterBottom>
                    Deliverable
                  </Typography>
                  {isLoadingStats ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stats?.deliverable ?? 0}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Completely Fresh Leads */}
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.12)', 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#ffffff'
              }}>
                <CardContent sx={{ p: '16px !important' }}>
                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 'medium' }} gutterBottom>
                    Never Ran
                  </Typography>
                  {isLoadingStats ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats?.never_ran ?? 0}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Deliverability Classification yield detail */}
          {stats && (
            <Paper sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: '12px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0' 
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1.5 }}>
                🔍 Deliverability Classification Yield Breakdown
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { label: 'Deliverable', value: stats.deliverable, color: '#10b981', icon: <CheckedIcon sx={{ fontSize: 16 }} /> },
                  { label: 'Risky', value: stats.risky, color: '#f59e0b', icon: <EmailIcon sx={{ fontSize: 16 }} /> },
                  { label: 'Undeliverable', value: stats.undeliverable, color: '#ef4444', icon: <BlockIcon sx={{ fontSize: 16 }} /> },
                  { label: 'Bounce', value: stats.bounce, color: '#dc2626', icon: <BlockIcon sx={{ fontSize: 16 }} /> },
                  { label: 'Unknown', value: stats.unknown, color: '#9ca3af', icon: <EmailIcon sx={{ fontSize: 16 }} /> },
                  { label: 'Not Verified', value: stats.not_verified, color: '#cbd5e1', icon: <EmailIcon sx={{ fontSize: 16 }} /> },
                ].map((item, idx) => (
                  <Grid item xs={4} sm={2} key={idx}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: '8px', 
                      backgroundColor: '#f9fafb',
                      borderLeft: `4px solid ${item.color}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2, fontWeight: 'medium' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 0.5, color: '#1a1a1a' }}>
                        {item.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Preview Grid */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: '12px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📋 Matching Leads Preview (Limit 50)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isLoadingStats ? 'Updating...' : `Showing ${previewRows.length} domains`}
              </Typography>
            </Box>

            {isLoadingStats ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : previewRows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Typography variant="body1" gutterBottom>No company domains match this query</Typography>
                <Typography variant="body2">Try loosening your search filters on the left.</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 380, borderRadius: '8px', border: '1px solid #eaeaea' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Company Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Website</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }} align="right">Inmailers</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Verified</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.name || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                          {row.website}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.location || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {row.in_mailer ? (
                            <Chip 
                              label={row.in_mailer} 
                              size="small" 
                              color="secondary" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.75rem', 
                                fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' 
                              }} 
                            />
                          ) : '0'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {row.last_ran_gmo && (
                              <Chip label="GMO" size="small" variant="outlined" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                            )}
                            {row.last_ran_snov && (
                              <Chip label="Snov" size="small" variant="outlined" color="primary" sx={{ height: 16, fontSize: '0.65rem' }} />
                            )}
                            {row.last_ran_reoon && (
                              <Chip label="Reoon" size="small" variant="outlined" color="warning" sx={{ height: 16, fontSize: '0.65rem' }} />
                            )}
                            {!row.last_ran_gmo && !row.last_ran_snov && !row.last_ran_reoon && (
                              <Chip label="Never" size="small" variant="outlined" color="default" sx={{ height: 16, fontSize: '0.65rem', borderStyle: 'dashed' }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OutreachLeadFinder;
